import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { DECKS, type DeckCard, type DeckId } from "@/data/recovery-board-cards";
import { DECK_BACKS } from "@/data/recovery-board-backs";

type Props = {
  deck: DeckId;
  card: DeckCard;
  onDone: (accepted: boolean) => void;
};

export function DrawCard({ deck, card, onDone }: Props) {
  const d = DECKS[deck];
  const [flipped, setFlipped] = useState(false);
  const [checked, setChecked] = useState<number[]>([]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onDone(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);
  const isReflection = deck === "reflection";
  const isGrounding = deck === "grounding";
  const acceptLabel = isReflection ? "I reflected 💛 (+1)" : isGrounding ? `I tried it (+${card.reward ?? 1})`
    : card.effect?.move ? `Move ${card.effect.move} forward` : card.effect?.rollAgain ? "Roll again" : `Collect +${card.reward ?? 0} 🌟`;

  return (
    <div role="dialog" aria-modal="true" aria-label={d.name}
      onClick={() => onDone(false)}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/40 p-4 pb-24 motion-safe:animate-in motion-safe:fade-in sm:pb-4">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xs motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:slide-in-from-bottom-10 motion-safe:duration-500" style={{ perspective: "1200px" }}>
        <div className="relative transition-transform duration-700 ease-out"
          style={{ transformStyle: "preserve-3d", transform: flipped ? "rotateY(180deg)" : "none", minHeight: "26rem" }}>
          {/* Back of card */}
          <button type="button" onClick={() => setFlipped(true)} aria-label={`Flip the ${d.name}`}
            className="absolute inset-0 flex flex-col items-center justify-end overflow-hidden rounded-3xl border-4 border-[var(--gold)] bg-card p-3 pb-16 text-center shadow-2xl"
            style={{ backfaceVisibility: "hidden" }}>
            <img src={DECK_BACKS[deck]} alt="" width={688} height={960} className="absolute inset-0 h-full w-full object-cover" />
            <span className="relative w-full rounded-2xl bg-card/95 px-3 py-2.5 shadow-lg">
              <span className="block font-serif text-xl text-foreground">{d.emoji} {d.name}</span>
              <span className="mt-0.5 block text-[13px] font-medium leading-snug text-foreground">{d.blurb}</span>
              <span className="rt-rb-cta mt-2 inline-block rounded-full px-5 py-1.5 text-sm font-semibold shadow">Tap to flip ✨</span>
            </span>
          </button>
          {!flipped && (
            <button type="button" onClick={() => onDone(false)}
              className="absolute inset-x-0 bottom-3 z-10 mx-auto w-fit rounded-full border border-border bg-card/90 px-4 py-2 text-xs font-semibold text-foreground">
              ✕ Not now — close card
            </button>
          )}

          {/* Face of card */}
          <div className="absolute inset-0 flex flex-col overflow-y-auto rounded-3xl border-4 border-[var(--gold)] bg-card p-5 shadow-2xl"
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }} aria-hidden={!flipped}>
            <p className="text-[13px] font-semibold uppercase tracking-wider text-[var(--gold-deep)]">{d.emoji} {d.name}</p>
            <p className="mt-3 font-serif text-xl leading-snug text-foreground">{card.text}</p>
            {card.steps && (
              <ul className="mt-3 space-y-1.5">
                {card.steps.map((s, i) => (
                  <li key={s}>
                    <button type="button" tabIndex={flipped ? 0 : -1} aria-pressed={checked.includes(i)} data-saved={checked.includes(i) || undefined}
                      onClick={() => setChecked(checked.includes(i) ? checked.filter((c) => c !== i) : [...checked, i])}
                      className="rt-choice flex min-h-10 w-full items-center gap-2 rounded-lg border border-border bg-background px-3 text-left text-sm text-foreground">
                      <span aria-hidden>{checked.includes(i) ? "✓" : "○"}</span> {s}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {isReflection && <p className="mt-3 text-xs font-medium text-foreground/85">Just think quietly — nothing is written down or saved. If this question doesn't feel right today, skip it. No penalty.</p>}
            {card.link && (
              <Link to={card.link.to as never} tabIndex={flipped ? 0 : -1} className="mt-3 text-sm text-primary underline underline-offset-2">
                Explore: {card.link.label} →
              </Link>
            )}
            <div className="mt-auto flex flex-col gap-2 pt-4">
              <button type="button" tabIndex={flipped ? 0 : -1} onClick={() => onDone(true)}
                className="rt-rb-cta min-h-11 rounded-full px-4 text-sm font-semibold shadow">
                {acceptLabel}
              </button>
              <button type="button" tabIndex={flipped ? 0 : -1} onClick={() => onDone(false)}
                className="min-h-11 rounded-full border border-border px-4 text-sm text-foreground">
                {isReflection || isGrounding ? "Skip for now — that's okay" : "✕ Close card — no penalty"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
